// Store Zustand auth — tokens + user + tenant courant, persisté via localStorage.
//
// Le store est branché sur le client fetch via `configureApiClient` pour que
// la rotation de token soit transparente.

import { create } from "zustand";
import { persist } from "zustand/middleware";

import { authApi, type LoginPayload, type SignupPayload } from "../api/auth";
import { configureApiClient } from "../api/client";
import type {
  AuthResponse,
  MembershipPublic,
  TenantPlanInfo,
  TenantPublic,
  UserPublic,
  UserRole,
  UUID,
} from "../api/types";

type AuthState = {
  user: UserPublic | null;
  memberships: MembershipPublic[];
  currentTenant: TenantPublic | null;
  currentRole: UserRole | null;
  // Lot 8.3 — plan + trial du tenant courant. null si pas de tenant scopé
  // ou si le backend n'a pas renvoyé l'info (anciennes versions).
  planInfo: TenantPlanInfo | null;
  accessToken: string | null;
  refreshToken: string | null;
  accessExpiresAt: string | null;

  signup: (payload: SignupPayload) => Promise<void>;
  login: (payload: LoginPayload) => Promise<void>;
  selectTenant: (tenantId: UUID) => Promise<void>;
  refreshMe: () => Promise<void>;
  logout: () => Promise<void>;
};

const initialState = {
  user: null,
  memberships: [] as MembershipPublic[],
  currentTenant: null,
  currentRole: null,
  planInfo: null as TenantPlanInfo | null,
  accessToken: null,
  refreshToken: null,
  accessExpiresAt: null,
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      ...initialState,

      async signup(payload) {
        const resp = await authApi.signup(payload);
        applyAuthResponse(set, resp);
      },

      async login(payload) {
        const resp = await authApi.login(payload);
        applyAuthResponse(set, resp);
      },

      async selectTenant(tenantId) {
        const tokens = await authApi.selectTenant(tenantId);
        const user = get().user;
        const memberships = get().memberships;
        const m = memberships.find((mm) => mm.tenant.id === tenantId) ?? null;
        set({
          accessToken: tokens.access_token,
          accessExpiresAt: tokens.expires_at,
          // refresh_token peut être null sur /select-tenant (on garde le précédent)
          refreshToken: tokens.refresh_token ?? get().refreshToken,
          currentTenant: m?.tenant ?? null,
          currentRole: m?.role ?? null,
          // /select-tenant ne renvoie que les tokens — on nettoie planInfo
          // et on le rechargera via refreshMe() juste après côté appelant.
          planInfo: null,
          user,
        });
        // Recharge /me pour obtenir plan_info du nouveau tenant.
        try {
          await get().refreshMe();
        } catch {
          /* si /me échoue, on reste avec planInfo=null — la bannière
             réapparaîtra au prochain refreshMe, pas bloquant */
        }
      },

      async refreshMe() {
        const me = await authApi.me();
        set({
          user: me.user,
          memberships: me.memberships,
          currentTenant: me.current_tenant,
          currentRole: me.current_role,
          planInfo: me.plan_info,
        });
      },

      async logout() {
        try {
          await authApi.logout();
        } catch {
          /* l'API peut déjà être down ; on nettoie quand même */
        }
        set({ ...initialState });
      },
    }),
    {
      name: "hinvoice.auth",
      partialize: (s) => ({
        user: s.user,
        memberships: s.memberships,
        currentTenant: s.currentTenant,
        currentRole: s.currentRole,
        planInfo: s.planInfo,
        accessToken: s.accessToken,
        refreshToken: s.refreshToken,
        accessExpiresAt: s.accessExpiresAt,
      }),
    },
  ),
);

function applyAuthResponse(
  set: (partial: Partial<AuthState>) => void,
  resp: AuthResponse,
) {
  const current = resp.current_tenant;
  const role =
    resp.memberships.find((m) => current && m.tenant.id === current.id)?.role ??
    null;
  set({
    user: resp.user,
    memberships: resp.memberships,
    currentTenant: current,
    currentRole: role,
    planInfo: resp.plan_info,
    accessToken: resp.tokens.access_token,
    refreshToken: resp.tokens.refresh_token,
    accessExpiresAt: resp.tokens.expires_at,
  });
}

// ---- Branchement client fetch ↔ store ---------------------------------------
// Appelé une fois au démarrage de l'app (dans main.tsx).
export function wireAuthClient(): void {
  configureApiClient({
    getAccessToken: () => useAuthStore.getState().accessToken,
    getRefreshToken: () => useAuthStore.getState().refreshToken,
    onRefreshed: (payload) =>
      useAuthStore.setState({
        accessToken: payload.access_token,
        accessExpiresAt: payload.expires_at,
        refreshToken: payload.refresh_token,
      }),
    onAuthExpired: () =>
      useAuthStore.setState({ ...initialState }),
  });
}

// Sélecteurs utiles pour les composants.
export const selectIsAuthenticated = (s: AuthState) =>
  s.accessToken !== null && s.user !== null;

export const selectNeedsTenant = (s: AuthState) =>
  s.user !== null && s.currentTenant === null && s.memberships.length > 1;

// Lot 8.3 — plan + trial du tenant courant. null si non-authentifié ou
// si un tenant n'a pas encore été sélectionné.
export const selectPlanInfo = (s: AuthState) => s.planInfo;
